/**
 * Inventory Service
 *
 * Handles inventory management operations including CRUD operations,
 * stock movements (entry/exit/adjustment), and low stock alerts.
 *
 * @module services/inventoryService
 */

import { supabase } from './supabase';
import { normalizarTexto, terminoParaFiltro, columnaNormalizada } from '../utils/normalizacionBusqueda';
import { MOVEMENT_TYPE, STORAGE_BUCKETS } from '../utils/constants';
// #568 — NUNCA se persiste una URL (firmada ni pública): se guarda la ruta del
// objeto y se firma FRESCO en cada lectura. Ver la cabecera de
// `src/utils/toolImageStorage.js` (gemelo del carril MCP).
import {
  canonicalImageValue,
  extractStoragePath,
  resolveImageValue,
  resolveRowsImageField
} from '../utils/toolImageStorage';
import { isBucketPublic } from '../utils/storageBucketPublicity';
import { isLowStock } from '../utils/calculations';

/**
 * Ceiling on rows pulled when "bajo stock" has to be resolved client-side
 * (#507 — see `listInventory`). Prod holds ~61 inventory items, so this is
 * slack, not a real bound. It exists so the scan has an EXPLICIT limit we can
 * detect hitting, instead of silently inheriting PostgREST's default max-rows
 * and reporting a truncated set as if it were the whole inventory.
 */
const LOW_STOCK_SCAN_LIMIT = 1000;

/**
 * Embed de PostgREST que resuelve `inventory.default_location_id` contra
 * `tool_locations`, el catálogo compartido de lugares físicos.
 *
 * Decisión 8 (Jorge, 2026-07-27): "por defecto todo cae a la bodega principal a
 * menos que se indique otro lugar". El default lo pone la DB (DEFAULT de
 * columna + trigger de coerción, migración 20260727220000) — acá solo se LEE,
 * para que la GUI muestre la misma bodega que el agente le dice al usuario por
 * el carril MCP. Sin este embed el web app sería el único lugar donde la
 * ubicación no existe, que es la asimetría de carriles que ya costó #353.
 */
export const INVENTORY_DEFAULT_LOCATION_SELECT =
  'default_location:default_location_id(id, code, label, type)';

/**
 * Columnas del catálogo que NO llevan plata (#522).
 *
 * `listInventory` usaba `select('*')`, que arrastra `unit_cost` —lo que
 * ProMedic PAGÓ— y `sale_price` a todo el que llame. Y la llaman tres caminos
 * que el técnico alcanza: la búsqueda de materiales de su solicitud
 * (`MaterialSearch`), el modal de agregar material a una tarea
 * (`AddMaterialModal`) y la búsqueda global. Ninguno de los tres muestra
 * precios; los recibían igual, y en la pestaña de red el dato está.
 *
 * Es exactamente el defecto de #505 en otra tabla: no lo pintaba nadie, y
 * viajaba igual.
 *
 * `avg_cost` y `weighted_avg_cost` no figuran acá porque no son columnas de
 * `inventory` — se derivan de los lotes en `getInventoryValuation`, que es
 * admin-only por ruta.
 */
const INVENTORY_COLUMNS_SIN_COSTO = [
  'id', 'sku', 'name', 'description', 'unit',
  'current_stock', 'min_stock_level',
  'image_url', 'category', 'is_active',
  'track_batches', 'item_type', 'is_stockable',
  'default_location_id', 'created_at', 'updated_at',
].join(', ');

/** Las dos que sí llevan plata. Sólo para quien las pide explícitamente. */
const INVENTORY_COLUMNS_DE_COSTO = 'unit_cost, sale_price';

/**
 * Orden aceptado por `listInventory`. Ordenar es tan "antes de paginar" como
 * filtrar: si la página se recorta primero y se ordena después, lo que se ve es
 * el orden de 20 filas arbitrarias, no las 20 primeras del inventario.
 */
const INVENTORY_SORTS = {
  name: { column: 'name', ascending: true },
  sku: { column: 'sku', ascending: true },
  'stock-asc': { column: 'current_stock', ascending: true },
  'stock-desc': { column: 'current_stock', ascending: false },
};

/**
 * List inventory items with optional filters
 * @param {Object} filters - Filter options
 * @param {string} filters.search - Search by name or SKU
 * @param {string} filters.category - Categoría exacta (`inventory.category`)
 * @param {'material'|'tool'} filters.itemType - Pestaña Materiales / Herramientas.
 *   `material` incluye las filas con `item_type` NULL (histórico).
 * @param {string} filters.sortBy - Clave de `INVENTORY_SORTS` (default 'name')
 * @param {boolean} filters.lowStock - Filter items with low stock
 * @param {number} filters.page - Page number (0-indexed)
 * @param {number} filters.pageSize - Items per page
 * @returns {Promise<{data, count, error}>}
 */
export const listInventory = async (filters = {}) => {
  try {
    // #522 — el costo no viaja salvo que el llamador lo pida.
    //
    // Antes era `select('*')`, que traía `unit_cost` y `sale_price` a los tres
    // caminos que alcanza el técnico (MaterialSearch, AddMaterialModal,
    // búsqueda global). Ninguno los muestra; los recibían igual.
    //
    // El opt-in es explícito y no automático por rol a propósito: un servicio
    // que decide permisos por su cuenta esconde la regla. Acá el llamador
    // declara que necesita plata, y quienes lo hacen son pantallas admin-only
    // por `RoleGuard` (InventoryList, PurchaseForm). Si mañana una pantalla de
    // técnico pide `includeCosts`, se ve en el diff.
    const columnas = filters.includeCosts
      ? `${INVENTORY_COLUMNS_SIN_COSTO}, ${INVENTORY_COLUMNS_DE_COSTO}`
      : INVENTORY_COLUMNS_SIN_COSTO;

    let query = supabase
      .from('inventory')
      .select(`${columnas}, ${INVENTORY_DEFAULT_LOCATION_SELECT}`, { count: 'exact' });

    // Exclude items that were deactivated (e.g. moved to the Tools module):
    // show active (true) and legacy null flags, hide explicit false. See #415.
    query = query.or('is_active.is.null,is_active.eq.true');

    // Apply filters
    if (filters.search) {
      // #739/#742 — término del módulo compartido; el texto humano va contra su
      // columna generada y los códigos y emails quedan crudos.
      const termino = terminoParaFiltro(filters.search);
      query = query.or(
        `${columnaNormalizada('name')}.ilike.%${termino}%,`
        + `sku.ilike.%${termino}%,`
        + `${columnaNormalizada('description')}.ilike.%${termino}%`
      );
    }

    // La categoría llegaba desde InventoryList y este servicio la ignoraba por
    // completo: elegir "Pintura" no cambiaba nada y no había señal de que el
    // filtro fuera inerte.
    if (filters.category) {
      // #737/#742 — CAMBIO DE CONDUCTA DECLARADO: `category` deja de ser un valor
      // y pasa a ser una clase de equivalencia.
      //
      // Era `.eq` exacto contra la columna cruda, y el dato esta guardado
      // "Electrico" SIN tilde: medido contra la base, `category = 'electrico'`
      // devolvia 0 y `category_norm = 'electrico'` devuelve 15. Cero de quince,
      // escribiendolo BIEN — la ortografia correcta del espanol era la que
      // fallaba, y en silencio.
      //
      // Ahora "electrico", "Electrico", "ELECTRICO" y "electrico" son la misma
      // categoria. Lo que se pierde: dos categorias que difirieran SOLO en
      // tildes dejarian de distinguirse. Medido: ese caso no existe en esta
      // base, y una base que distingue dos categorias por una tilde ya tiene un
      // problema de datos.
      //
      // NO aplica a `tasks.category` ni `expenses.category`: esos son enums
      // ASCII que la UI elige de una lista, y ahi la igualdad exacta es lo
      // correcto.
      query = query.eq(columnaNormalizada('category'), normalizarTexto(filters.category));
    }

    // Pestañas Materiales / Herramientas. Vivía como `.filter()` en el
    // componente, DESPUÉS del recorte de página: la pestaña Herramientas
    // mostraba solo las herramientas que hubieran caído dentro de las primeras
    // 20 filas del inventario completo, y el conteo de la cabecera contaba esa
    // sobra en lugar del total real.
    if (filters.itemType === 'tool') {
      query = query.eq('item_type', 'tool');
    } else if (filters.itemType === 'material') {
      query = query.or('item_type.is.null,item_type.neq.tool');
    }

    // Apply pagination
    const page = filters.page || 0;
    const pageSize = filters.pageSize || 20;
    const from = page * pageSize;
    const to = from + pageSize - 1;

    const sort = INVENTORY_SORTS[filters.sortBy] || INVENTORY_SORTS.name;
    query = query.order(sort.column, { ascending: sort.ascending });

    // #507: "bajo stock" cannot be a query-side filter.
    //
    // This used to be `.lt('current_stock', supabase.raw('min_stock_level'))`.
    // `.raw()` is a supabase-js v1 / knex-ism that does not exist on the v2
    // client we ship (`^2.58.0`) — `typeof supabase.raw === 'undefined'` — so
    // the call threw a TypeError before touching the network. The `catch` below
    // swallowed it into `{ data: null, error }`, and the one caller
    // (MaterialSearch's "Stock bajo" filter) rendered that as an empty result
    // list. It never looked like a crash; it looked like "no hay materiales con
    // bajo stock", which is exactly why it survived.
    //
    // There is no query-side fix to reach for either: PostgREST has no
    // column-to-column comparison operator, so `current_stock <= min_stock_level`
    // simply cannot be expressed as a filter. The MCP lanes resolved this the
    // same way — pull the rows, decide in JS with the shared `isLowStock()`.
    //
    // Pagination therefore has to move AFTER the filter. Ranging first would
    // page over the unfiltered table and then filter only that page, so page 1
    // could come back with 3 of 20 rows while page 2 still held matches, and
    // `count` would report the size of the whole inventory instead of the size
    // of the low-stock set.
    if (filters.lowStock) {
      const { data, error } = await query.limit(LOW_STOCK_SCAN_LIMIT);

      if (error) throw error;

      const scanned = data || [];

      // No silent truncation: if we ever hit the ceiling, the filtered count
      // below is a floor, not the answer.
      if (scanned.length === LOW_STOCK_SCAN_LIMIT) {
        console.warn(
          `[inventory] low-stock scan hit the ${LOW_STOCK_SCAN_LIMIT}-row ceiling; ` +
          'results and count may be incomplete. Move this filter to an RPC/view.'
        );
      }

      const matching = scanned.filter(
        (item) => isLowStock(item.current_stock, item.min_stock_level)
      );

      const page = matching.slice(from, from + pageSize);
      // #568 — `image_url` guarda la ruta del objeto; se firma al leer.
      await resolveRowsImageField(supabase, page, await inventoryImageOpts());
      return {
        data: page,
        // The count of the FILTERED set — what the pager has to divide.
        count: matching.length,
        error: null
      };
    }

    const { data, count, error } = await query.range(from, to);

    if (error) throw error;

    // #568 — ver arriba: lo guardado es la ruta, la URL se firma acá.
    await resolveRowsImageField(supabase, data || [], await inventoryImageOpts());

    return { data, count, error: null };
  } catch (error) {
    console.error('List inventory error:', error);
    return { data: null, count: 0, error };
  }
};

/**
 * #568 — opciones de resolución de imágenes del carril web.
 *
 * `isPublic` sale de la publicidad REAL del bucket leída en runtime, no de una
 * lista escrita en código (`avatars` es público, `inventory-images` privado, y
 * `chat-attachments` cambió de lado). Ver `utils/storageBucketPublicity.js`.
 */
export const inventoryImageOpts = async () => ({
  bucket: STORAGE_BUCKETS.INVENTORY_IMAGES,
  isPublic: await isBucketPublic(STORAGE_BUCKETS.INVENTORY_IMAGES),
  onWarn: (msg, meta) => console.warn(`[inventory-images] ${msg}`, meta || '')
});

/**
 * Get inventory item by ID with recent movements
 * @param {string} inventoryId - Inventory item ID
 * @returns {Promise<{data, error}>}
 */
export const getInventoryItem = async (inventoryId) => {
  try {
    const { data, error } = await supabase
      .from('inventory')
      .select(`
        *,
        ${INVENTORY_DEFAULT_LOCATION_SELECT},
        stock_movements(
          id,
          movement_type,
          quantity,
          notes,
          created_at,
          created_by_profile:profiles!created_by(id, full_name)
        )
      `)
      .eq('id', inventoryId)
      .order('created_at', { foreignTable: 'stock_movements', ascending: false })
      .limit(10, { foreignTable: 'stock_movements' })
      .single();

    if (error) throw error;

    // #568 — lo guardado es la ruta; la URL se firma acá, al leer.
    if (data?.image_url) {
      data.image_url = await resolveImageValue(supabase, data.image_url, await inventoryImageOpts());
    }

    return { data, error: null };
  } catch (error) {
    console.error('Get inventory item error:', error);
    return { data: null, error };
  }
};

/**
 * Create new inventory item
 * @param {Object} itemData - Inventory item data
 * @param {string} itemData.name - Item name
 * @param {string} itemData.sku - Stock keeping unit (optional)
 * @param {string} itemData.description - Item description (optional)
 * @param {string} itemData.unit - Unit of measurement
 * @param {number} itemData.unit_cost - Cost per unit
 * @param {number} itemData.current_stock - Current stock quantity
 * @param {number} itemData.min_stock_level - Minimum stock level for alerts
 * @returns {Promise<{data, error}>}
 */
export const createInventoryItem = async (itemData) => {
  try {
    const { data, error } = await supabase
      .from('inventory')
      .insert(itemData)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Create inventory item error:', error);
    return { data: null, error };
  }
};

/**
 * Update inventory item
 * @param {string} inventoryId - Inventory item ID
 * @param {Object} updates - Item updates
 * @returns {Promise<{data, error}>}
 */
export const updateInventoryItem = async (inventoryId, updates) => {
  try {
    // #568 — si el llamador manda una URL en `image_url`, se reduce a la ruta
    // del objeto antes de persistir. Una URL firmada vence; la ruta no.
    const payload = updates?.image_url !== undefined
      ? { ...updates, image_url: canonicalImageValue(STORAGE_BUCKETS.INVENTORY_IMAGES, updates.image_url) }
      : updates;

    const { data, error } = await supabase
      .from('inventory')
      .update(payload)
      .eq('id', inventoryId)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Update inventory item error:', error);
    return { data: null, error };
  }
};

/**
 * Dar de baja un ítem del catálogo de inventario.
 *
 * NO BORRA LA FILA — escribe `is_active = false`.
 *
 * Esto hacía `.delete().eq('id', id)`, un DELETE físico, y de la fila de
 * `inventory` cuelga el ledger:
 *
 *   stock_movements.inventory_id        ON DELETE CASCADE
 *   material_request_items.inventory_id ON DELETE SET NULL
 *   material_requests.inventory_id      ON DELETE SET NULL
 *   quotation_items.inventory_id        ON DELETE SET NULL
 *
 * O sea que borrar un ítem destruía su libro de movimientos (entradas,
 * salidas, consumos FIFO, ajustes, cada uno con su costo al momento —
 * evidencia contable) y dejaba las solicitudes y las líneas de cotización
 * apuntando a la nada: una solicitud que ya no dice qué material era.
 *
 * `is_active` ya existe y ya se usa exactamente con este propósito: la
 * migración de implementos de pintura del 28-jul dio de baja 7 filas así,
 * justamente porque `quotation_items` referencia el historial.
 *
 * También arrastraba el defecto de #529: sin `.select()`, PostgREST responde
 * 204 con `error: null` aunque la RLS haya filtrado la fila y no se haya
 * tocado nada. `success: true` sobre un ítem que sigue igual.
 *
 * Paridad: el carril MCP hace lo mismo en
 * `mcp-server/src/tools/parityTools.js` → `deleteInventoryItem`. El carril
 * stdio (`mcp-server/src/mcp-server.js`) no expone borrado de inventario.
 *
 * @param {string} inventoryId - Inventory item ID
 * @returns {Promise<{success, error}>}
 */
export const deleteInventoryItem = async (inventoryId) => {
  try {
    const { data, error } = await supabase
      .from('inventory')
      .update({ is_active: false })
      .eq('id', inventoryId)
      .select('id');

    if (error) throw error;

    if (!data || data.length === 0) {
      throw new Error(
        'No se dio de baja el ítem: no existe o no tenés permiso para modificarlo. Sigue activo.'
      );
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Deactivate inventory item error:', error);
    return { success: false, error };
  }
};

/**
 * Record stock movement (entry, exit, adjustment)
 *
 * Issue #394: the 'exit' case used to insert a NEGATIVE quantity
 * (`-Math.abs(quantity)`) into stock_movements, but
 * update_inventory_on_stock_movement's 'exit' branch does
 * `current_stock = current_stock - NEW.quantity` -- subtracting a negative
 * ADDS it, so every manual exit recorded from the web UI on a
 * non-batch-tracked item (StockMovementModal's `track_batches === false`
 * lane) made current_stock go UP instead of down. It now inserts a POSITIVE
 * quantity magnitude, matching the #287 convention already used by
 * consume_inventory_fifo / consume_specific_batches and by the MCP lane, and
 * leaving the trigger as the sole (correctly signed) writer of current_stock.
 * The signed delta is still computed separately for the local
 * insufficient-stock precheck.
 *
 * @param {Object} movementData - Stock movement data
 * @param {string} movementData.inventory_id - Inventory item ID
 * @param {string} movementData.movement_type - Movement type (entry/exit/adjustment)
 * @param {number} movementData.quantity - Quantity moved. Magnitude for
 *        entry/exit (the movement type carries the direction); signed delta
 *        for adjustment (can be positive or negative)
 * @param {string} movementData.reference_type - Reference type (project, quotation, etc.)
 * @param {string} movementData.reference_id - Reference ID (optional)
 * @param {string} movementData.notes - Movement notes (optional)
 * @returns {Promise<{data, updatedInventory, error}>}
 */
export const recordStockMovement = async (movementData) => {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    // Get current inventory item
    const { data: inventoryItem, error: inventoryError } = await supabase
      .from('inventory')
      .select('current_stock')
      .eq('id', movementData.inventory_id)
      .single();

    if (inventoryError) throw inventoryError;

    // Two distinct values, do not conflate them:
    //  - stockDelta: signed effect on current_stock, used only for the local
    //    insufficient-stock precheck below.
    //  - movementQuantity: what actually goes into stock_movements.quantity.
    //    The movement_type carries the direction, so entry/exit both store a
    //    positive magnitude and let the trigger apply the sign (#287/#394).
    let stockDelta = 0;
    let movementQuantity = 0;
    switch (movementData.movement_type) {
      case MOVEMENT_TYPE.ENTRY:
        movementQuantity = Math.abs(movementData.quantity);
        stockDelta = movementQuantity;
        break;
      case MOVEMENT_TYPE.EXIT:
        movementQuantity = Math.abs(movementData.quantity);
        stockDelta = -movementQuantity;
        break;
      case MOVEMENT_TYPE.ADJUSTMENT:
        // Signed delta, applied as-is by the trigger (see #394)
        movementQuantity = movementData.quantity; // Can be positive or negative
        stockDelta = movementQuantity;
        break;
      default:
        throw new Error('Invalid movement type');
    }

    const newStock = inventoryItem.current_stock + stockDelta;

    if (newStock < 0) {
      throw new Error('Stock insuficiente para realizar esta operación');
    }

    // `valid_exit_movement` (CHECK en prod):
    //   (movement_type='exit' AND project_id IS NOT NULL)
    //   OR movement_type IN ('entry','adjustment')
    // El insert de abajo NO mandaba `project_id`, así que TODA salida
    // registrada por esta vía rebotaba. Y como los 61 ítems de inventario
    // tienen `track_batches=false`, todas las salidas de la GUI caen acá:
    // bajar stock desde la web era imposible.
    if (movementData.movement_type === MOVEMENT_TYPE.EXIT && !movementData.project_id) {
      throw new Error(
        'Toda salida de stock debe indicar el proyecto al que se carga (project_id).'
      );
    }

    // Insert stock movement (only include columns that exist in DB)
    const { data: movement, error: movementError } = await supabase
      .from('stock_movements')
      .insert({
        inventory_id: movementData.inventory_id,
        movement_type: movementData.movement_type,
        quantity: movementQuantity,
        project_id: movementData.project_id || null,
        task_id: movementData.task_id || null,
        notes: movementData.notes || null,
        created_by: user.id,
      })
      .select(`
        *,
        inventory:inventory(id, name, sku),
        created_by_profile:profiles!created_by(id, full_name)
      `)
      .single();

    if (movementError) throw movementError;

    // Update inventory stock (handled by database trigger)
    // We still fetch the updated inventory to return
    const { data: updatedInventory, error: updateError } = await supabase
      .from('inventory')
      .select('*')
      .eq('id', movementData.inventory_id)
      .single();

    if (updateError) throw updateError;

    return {
      data: movement,
      updatedInventory,
      error: null,
    };
  } catch (error) {
    console.error('Record stock movement error:', error);
    return { data: null, updatedInventory: null, error };
  }
};

/**
 * Get stock movements for an inventory item
 * @param {string} inventoryId - Inventory item ID
 * @param {Object} filters - Filter options
 * @param {string} filters.movementType - Filter by movement type
 * @param {string} filters.dateFrom - Start date (YYYY-MM-DD)
 * @param {string} filters.dateTo - End date (YYYY-MM-DD)
 * @param {number} filters.limit - Limit number of results
 * @returns {Promise<{data, error}>}
 */
export const getStockMovements = async (inventoryId, filters = {}) => {
  try {
    let query = supabase
      .from('stock_movements')
      .select(`
        *,
        inventory:inventory(id, name, sku),
        created_by_profile:profiles!created_by(id, full_name)
      `)
      .eq('inventory_id', inventoryId);

    // Apply filters
    if (filters.movementType) {
      query = query.eq('movement_type', filters.movementType);
    }

    if (filters.dateFrom) {
      query = query.gte('created_at', filters.dateFrom);
    }

    if (filters.dateTo) {
      query = query.lte('created_at', filters.dateTo);
    }

    query = query.order('created_at', { ascending: false });

    if (filters.limit) {
      query = query.limit(filters.limit);
    }

    const { data, error } = await query;

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Get stock movements error:', error);
    return { data: null, error };
  }
};

/**
 * Get low stock items
 * @param {number} threshold - Optional threshold multiplier (default 1.0)
 * @returns {Promise<{data, error}>}
 */
export const getLowStockItems = async (threshold = 1.0) => {
  try {
    // #507: same `supabase.raw()` v1-ism as `listInventory`, same TypeError,
    // same swallow — this one powers the "solo bajo stock" toggle in
    // InventoryList, which therefore showed an empty table to every admin who
    // used it. The threshold multiplier moves into the JS comparison:
    // `isLowStock(current, min * threshold)` is `current <= min * threshold`,
    // which at the default threshold of 1.0 is exactly the shared rule the MCP
    // lanes apply.
    const { data, error } = await supabase
      .from('inventory')
      // Mismo embed que `listInventory`: InventoryList alterna entre las dos
      // fuentes con el toggle "solo bajo stock" y la columna Bodega tiene que
      // seguir mostrando lo mismo en los dos modos.
      .select(`*, ${INVENTORY_DEFAULT_LOCATION_SELECT}`)
      .order('current_stock', { ascending: true })
      .limit(LOW_STOCK_SCAN_LIMIT);

    if (error) throw error;

    const scanned = data || [];

    if (scanned.length === LOW_STOCK_SCAN_LIMIT) {
      console.warn(
        `[inventory] low-stock scan hit the ${LOW_STOCK_SCAN_LIMIT}-row ceiling; ` +
        'the returned set may be incomplete. Move this filter to an RPC/view.'
      );
    }

    // Calculate stock status for each item
    const itemsWithStatus = scanned
      .filter((item) => isLowStock(item.current_stock, item.min_stock_level * threshold))
      .map(item => {
        const stockPercentage = (item.current_stock / item.min_stock_level) * 100;
        let status = 'ok';
        if (item.current_stock === 0) {
          status = 'out_of_stock';
        } else if (stockPercentage < 50) {
          status = 'critical';
        } else if (isLowStock(item.current_stock, item.min_stock_level)) {
          // #507 — esto decia `stockPercentage < 100`, que es el MISMO borde
          // por el que existe `isLowStock`, escrito a mano una cuarta vez y con
          // el signo equivocado. El item que esta EXACTAMENTE en su minimo da
          // 100%, asi que pasaba el filtro de arriba (`<=`) y despues se
          // etiquetaba `ok`: aparecia en la lista de "bajo stock" con el badge
          // de que no lo estaba. Se compara con el helper compartido para que
          // el filtro y la etiqueta no puedan volver a discrepar.
          status = 'low';
        }

        return {
          ...item,
          stockPercentage: Math.round(stockPercentage),
          status,
        };
      });

    // #568 — lo guardado es la ruta; la URL se firma acá, al leer.
    await resolveRowsImageField(supabase, itemsWithStatus, await inventoryImageOpts());

    return { data: itemsWithStatus, error: null };
  } catch (error) {
    console.error('Get low stock items error:', error);
    return { data: null, error };
  }
};

/**
 * Get inventory summary statistics
 * @returns {Promise<{data, error}>}
 */
export const getInventorySummary = async () => {
  try {
    const { data: allItems, error } = await supabase
      .from('inventory')
      .select('current_stock, min_stock_level, unit_cost');

    if (error) throw error;

    const totalItems = allItems.length;
    // #507: same rule as the list and as both MCP lanes. This used to be a
    // hand-inlined `<`, so the "N artículos con bajo stock" badge could report
    // a smaller number than the low-stock list it links to — the boundary items
    // (current_stock === min_stock_level) were counted by one and not the other.
    const lowStockItems = allItems.filter(
      item => isLowStock(item.current_stock, item.min_stock_level)
    ).length;
    const outOfStockItems = allItems.filter(
      item => item.current_stock === 0
    ).length;
    const totalValue = allItems.reduce(
      (sum, item) => sum + (item.current_stock * item.unit_cost),
      0
    );

    return {
      data: {
        totalItems,
        lowStockItems,
        outOfStockItems,
        totalValue: Math.round(totalValue * 100) / 100,
      },
      error: null,
    };
  } catch (error) {
    console.error('Get inventory summary error:', error);
    return { data: null, error };
  }
};

/**
 * Bulk import inventory items
 * @param {Array} items - Array of inventory items
 * @returns {Promise<{data, errors, error}>}
 */
export const bulkImportInventory = async (items) => {
  try {
    const results = {
      success: [],
      errors: [],
    };

    for (const item of items) {
      const { data, error } = await createInventoryItem(item);
      if (error) {
        results.errors.push({ item, error: error.message });
      } else {
        results.success.push(data);
      }
    }

    return {
      data: results.success,
      errors: results.errors,
      error: null,
    };
  } catch (error) {
    console.error('Bulk import inventory error:', error);
    return { data: null, errors: null, error };
  }
};

/**
 * Upload inventory item image to Supabase Storage
 *
 * #568 — **`url` es para MOSTRAR AHORA, `path` es lo que se PERSISTE.**
 * Hasta este issue todos los llamadores guardaban `url` en la base, y `url` es
 * un enlace firmado a 1 hora: la foto de la herramienta cargaba mientras durara
 * la sesión y después quedaba un link muerto, sin error, sin aviso. Es el mismo
 * defecto de #332 en otra tabla. Si agregás un llamador, guardá `path`.
 *
 * @param {File} file - Image file to upload
 * @param {string} inventoryId - Inventory item ID for organizing files
 * @returns {Promise<{url, path, error}>} `url` efímero (display), `path` estable
 */
export const uploadInventoryImage = async (file, inventoryId) => {
  try {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Tipo de archivo no permitido. Use JPG, PNG o WebP.');
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error('El archivo excede el tamaño máximo de 5MB.');
    }

    const fileExt = file.name.split('.').pop();
    const fileName = `${inventoryId}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKETS.INVENTORY_IMAGES)
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) throw error;

    // Get signed URL (1-hour expiry, see CLAUDE.md storage policy)
    const { data: urlData, error: urlError } = await supabase.storage
      .from(STORAGE_BUCKETS.INVENTORY_IMAGES)
      .createSignedUrl(data.path, 3600);

    if (urlError) {
      // Fallback to public URL
      const { data: { publicUrl } } = supabase.storage
        .from(STORAGE_BUCKETS.INVENTORY_IMAGES)
        .getPublicUrl(data.path);
      return { url: publicUrl, path: data.path, error: null };
    }

    return { url: urlData.signedUrl, path: data.path, error: null };
  } catch (error) {
    console.error('Upload inventory image error:', error);
    return { url: null, path: null, error };
  }
};

/**
 * Delete inventory item image from Supabase Storage
 *
 * #568 — acepta las TRES formas que hay en la base: ruta pelada (lo que se
 * guarda desde este issue), URL firmada y URL `/object/public/...`. Antes
 * partía el string por el nombre del bucket y tiraba `Invalid image URL format`
 * con cualquier cosa que no fuera una URL — o sea que después de la migración
 * TODO borrado y TODO reemplazo de foto habría fallado.
 *
 * @param {string} imageUrl - ruta o URL de la imagen a borrar
 * @returns {Promise<{success, error}>}
 */
export const deleteInventoryImage = async (imageUrl) => {
  try {
    if (!imageUrl) return { success: true, error: null };

    const bucketName = STORAGE_BUCKETS.INVENTORY_IMAGES;
    const path = extractStoragePath(bucketName, imageUrl);
    if (!path) {
      throw new Error('Invalid image URL format');
    }

    const { error } = await supabase.storage
      .from(bucketName)
      .remove([path]);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error('Delete inventory image error:', error);
    return { success: false, error };
  }
};

/**
 * Update inventory item image
 * Uploads new image, updates DB, and deletes old image if exists
 * @param {string} inventoryId - Inventory item ID
 * @param {File} file - New image file
 * @param {string} oldImageUrl - URL of existing image to replace (optional)
 * @returns {Promise<{data, error}>}
 */
export const updateInventoryImage = async (inventoryId, file, oldImageUrl = null) => {
  try {
    // Upload new image
    const uploadResult = await uploadInventoryImage(file, inventoryId);
    if (uploadResult.error) throw uploadResult.error;

    // #568 — se persiste la RUTA (`path`), no la URL firmada (`url`). La URL
    // vence en 1 hora y la foto se cae sola; la ruta no vence nunca.
    const { data, error } = await supabase
      .from('inventory')
      .update({ image_url: uploadResult.path })
      .eq('id', inventoryId)
      .select()
      .single();

    if (error) {
      // Rollback: delete uploaded image
      await deleteInventoryImage(uploadResult.path);
      throw error;
    }

    // Delete old image if exists
    if (oldImageUrl) {
      await deleteInventoryImage(oldImageUrl);
    }

    // Lo persistido es la ruta; lo devuelto, una URL que se puede pintar ya.
    if (data?.image_url) {
      data.image_url = await resolveImageValue(supabase, data.image_url, await inventoryImageOpts());
    }

    return { data, error: null };
  } catch (error) {
    console.error('Update inventory image error:', error);
    return { data: null, error };
  }
};

/**
 * Remove image from inventory item
 * @param {string} inventoryId - Inventory item ID
 * @param {string} imageUrl - URL of image to remove
 * @returns {Promise<{success, error}>}
 */
export const removeInventoryImage = async (inventoryId, imageUrl) => {
  try {
    // Update inventory item to remove image URL
    const { error } = await supabase
      .from('inventory')
      .update({ image_url: null })
      .eq('id', inventoryId);

    if (error) throw error;

    // Delete image from storage
    await deleteInventoryImage(imageUrl);

    return { success: true, error: null };
  } catch (error) {
    console.error('Remove inventory image error:', error);
    return { success: false, error };
  }
};

/**
 * Movement reason types for stock movements
 */
export const MOVEMENT_REASONS = {
  PROJECT_CONSUMPTION: 'project_consumption',
  LOSS_DAMAGE: 'loss_damage',
  SUPPLIER_RETURN: 'supplier_return',
  TRANSFER: 'transfer',
  INVENTORY_ADJUSTMENT: 'inventory_adjustment',
  INITIAL_STOCK: 'initial_stock',
  PURCHASE: 'purchase',
};

/**
 * Movement reason labels for display
 */
export const MOVEMENT_REASON_LABELS = {
  [MOVEMENT_REASONS.PROJECT_CONSUMPTION]: 'Consumo en proyecto',
  [MOVEMENT_REASONS.LOSS_DAMAGE]: 'Merma / Perdida',
  [MOVEMENT_REASONS.SUPPLIER_RETURN]: 'Devolucion a proveedor',
  [MOVEMENT_REASONS.TRANSFER]: 'Transferencia',
  [MOVEMENT_REASONS.INVENTORY_ADJUSTMENT]: 'Ajuste de inventario',
  [MOVEMENT_REASONS.INITIAL_STOCK]: 'Stock inicial',
  [MOVEMENT_REASONS.PURCHASE]: 'Compra',
};

// Module-level flag: once we detect inventory_batches table is missing, skip all future queries
let _batchesTableMissing = false;

/**
 * Get inventory batches for an item
 * @param {string} inventoryId - Inventory item ID
 * @param {Object} options - Filter options
 * @param {boolean} options.activeOnly - Only return batches with available quantity
 * @returns {Promise<{data, error}>}
 */
export const getInventoryBatches = async (inventoryId, options = {}) => {
  // Skip query entirely if we already know the table doesn't exist
  if (_batchesTableMissing) {
    return { data: [], error: null };
  }

  try {
    // suppliers is reached via purchases (FK chain: inventory_batches -> purchases -> suppliers)
    let query = supabase
      .from('inventory_batches')
      .select(`
        *,
        purchase:purchases(id, invoice_number, supplier:suppliers(id, name))
      `)
      .eq('inventory_id', inventoryId)
      .order('created_at', { ascending: true }); // FIFO order

    if (options.activeOnly) {
      query = query.gt('quantity_available', 0);
    }

    const { data, error } = await query;

    if (error) {
      // Table doesn't exist yet — cache this and return empty silently
      if (error.code === 'PGRST205' || error.status === 404 || error.message?.includes('Could not find')) {
        _batchesTableMissing = true;
        return { data: [], error: null };
      }
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    // Also catch missing-table errors if they propagate through throw
    if (error?.code === 'PGRST205' || error?.status === 404 || error?.message?.includes('Could not find')) {
      _batchesTableMissing = true;
      return { data: [], error: null };
    }
    console.error('Get inventory batches error:', error);
    return { data: null, error };
  }
};

/**
 * Consume inventory using FIFO method
 * Consumes from oldest batches first
 *
 * Issue #394: this used to be a hand-rolled reimplementation of FIFO
 * consumption (loop of individual per-batch UPDATEs, non-atomic/race-prone)
 * that also inserted the 'exit' stock_movements row with a NEGATIVE
 * quantity (`-quantity`). update_inventory_on_stock_movement's 'exit'
 * branch does `current_stock = current_stock - NEW.quantity`, so a negative
 * NEW.quantity made current_stock go UP on every consumption instead of
 * down -- the opposite direction of what inventory_batches (correctly
 * decremented by the hand-rolled loop) was doing. It now delegates to the
 * atomic `consume_inventory_fifo` DB function (already fixed for the same
 * "several writers" pattern by issue #287, and already used by the MCP
 * lane's consumeExitViaFifo in mcp-server/src/tools/inventoryTools.js),
 * which keeps current_stock and inventory_batches in a single transaction
 * and lets update_inventory_stock_trigger be the sole writer of
 * current_stock -- restoring parity between the web and MCP lanes.
 *
 * @param {string} inventoryId - Inventory item ID
 * @param {number} quantity - Quantity to consume
 * @param {Object} options - Consumption options
 * @param {string} options.reason - Reason for consumption
 * @param {string} options.projectId - Project ID (for project consumption)
 * @param {string} options.taskId - Task ID (optional)
 * @param {string} options.notes - Additional notes
 * @returns {Promise<{data, consumedBatches, totalCost, error}>}
 */
export const consumeInventoryFIFO = async (inventoryId, quantity, options = {}) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { data, error } = await supabase.rpc('consume_inventory_fifo', {
      p_inventory_id: inventoryId,
      p_quantity: quantity,
      p_project_id: options.projectId || null,
      p_task_id: options.taskId || null,
      p_reason: options.reason || 'consumption',
      p_notes: options.notes || null,
      p_created_by: user.id,
    });

    if (error) throw error;

    const result = Array.isArray(data) ? data[0] : data;
    if (!result || !result.success) {
      throw new Error(result?.message || 'Failed to consume inventory via FIFO');
    }

    // Get updated inventory
    const { data: updatedInventory } = await supabase
      .from('inventory')
      .select('*')
      .eq('id', inventoryId)
      .single();

    return {
      data: result,
      consumedBatches: result.batches_affected,
      totalCost: Math.round((result.total_cost || 0) * 100) / 100,
      updatedInventory,
      error: null,
    };
  } catch (error) {
    console.error('Consume inventory FIFO error:', error);
    return { data: null, consumedBatches: null, totalCost: 0, error };
  }
};

/**
 * Adjust a specific batch quantity
 * @param {string} batchId - Batch ID
 * @param {Object} adjustment - Adjustment data
 * @param {number} adjustment.quantity - Quantity adjustment (positive or negative)
 * @param {string} adjustment.reason - Reason for adjustment
 * @param {string} adjustment.notes - Additional notes
 * @returns {Promise<{data, error}>}
 */
export const adjustBatch = async (batchId, adjustment) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    // Get current batch
    const { data: batch, error: batchError } = await supabase
      .from('inventory_batches')
      .select('*, inventory:inventory(id, name)')
      .eq('id', batchId)
      .single();

    if (batchError) throw batchError;

    const delta = Number(adjustment.quantity);
    const newQuantity = Number(batch.quantity_available) + delta;
    if (newQuantity < 0) {
      throw new Error('El ajuste resultaria en cantidad negativa');
    }

    // QUÉ ADMITE LA BASE (verificado por SELECT sobre pg_constraint, prod):
    //   check_entry_adjustment_positive → 'adjustment' EXIGE quantity > 0
    //   valid_exit_movement            → 'exit' EXIGE project_id NOT NULL
    // O sea que la única forma legal de bajar stock es un 'exit' atribuido a
    // un proyecto. Un ajuste negativo insertado como 'adjustment' rebota
    // SIEMPRE contra el CHECK — y antes ese rebote era un `console.error` y
    // la función devolvía éxito igual: el lote bajaba, `inventory.current_stock`
    // no se movía y no quedaba rastro del movimiento. Descuadre silencioso.
    if (delta < 0 && !adjustment.projectId) {
      throw new Error(
        'Para descontar del lote hay que indicar a qué proyecto se atribuye la salida: ' +
        'la base no acepta salidas de stock sin proyecto.'
      );
    }

    // Update batch
    const { data: updatedBatch, error: updateError } = await supabase
      .from('inventory_batches')
      .update({
        quantity_available: newQuantity,
        status: newQuantity === 0 ? 'depleted' : 'available',
      })
      .eq('id', batchId)
      .select()
      .single();

    if (updateError) throw updateError;

    // Record movement
    const isExit = delta < 0;
    const { error: movementError } = await supabase
      .from('stock_movements')
      .insert({
        inventory_id: batch.inventory_id,
        batch_id: batchId,
        movement_type: isExit ? MOVEMENT_TYPE.EXIT : MOVEMENT_TYPE.ADJUSTMENT,
        // El trigger aplica el signo según movement_type: 'exit' resta la
        // magnitud, 'adjustment' suma el delta tal cual.
        quantity: isExit ? Math.abs(delta) : delta,
        project_id: isExit ? adjustment.projectId : (adjustment.projectId || null),
        notes: `Ajuste de lote ${batch.batch_number}: ${adjustment.reason}. ${adjustment.notes || ''}`.trim(),
        created_by: user.id,
      });

    if (movementError) {
      // El movimiento es lo que mueve `inventory.current_stock`. Si no entró,
      // el lote NO puede quedarse bajado: se revierte y se reporta el error.
      await supabase
        .from('inventory_batches')
        .update({
          quantity_available: batch.quantity_available,
          status: batch.status,
        })
        .eq('id', batchId);
      throw movementError;
    }

    return { data: updatedBatch, error: null };
  } catch (error) {
    console.error('Adjust batch error:', error);
    return { data: null, error };
  }
};

/**
 * Get inventory valuation report
 * Calculates total value using FIFO and weighted average methods
 * @param {Object} options - Report options
 * @param {string} options.category - Filter by category
 * @returns {Promise<{data, totals, error}>}
 */
export const getInventoryValuation = async (options = {}) => {
  try {
    let items;
    let error;

    // Skip batch join entirely if we know the table doesn't exist
    if (_batchesTableMissing) {
      let query = supabase
        .from('inventory')
        .select('*');

      if (options.category) {
        // #737 — igualdad normalizada, ver el comentario largo de listInventory.
        query = query.eq(columnaNormalizada('category'), normalizarTexto(options.category));
      }

      const result = await query;
      if (result.error) throw result.error;
      items = (result.data || []).map(item => ({ ...item, batches: [] }));
    } else {
      // Get all inventory items with their batches
      let query = supabase
        .from('inventory')
        .select(`
          *,
          batches:inventory_batches(
            id,
            batch_number,
            quantity_available,
            unit_cost,
            status
          )
        `);

      if (options.category) {
        // #737 — igualdad normalizada, ver el comentario largo de listInventory.
        query = query.eq(columnaNormalizada('category'), normalizarTexto(options.category));
      }

      ({ data: items, error } = await query);

      // If inventory_batches table doesn't exist, fall back to query without batches
      if (error?.code === 'PGRST205') {
        _batchesTableMissing = true;
        let fallbackQuery = supabase
          .from('inventory')
          .select('*');

        if (options.category) {
          fallbackQuery = fallbackQuery.eq(columnaNormalizada('category'), normalizarTexto(options.category));
        }

        const fallbackResult = await fallbackQuery;
        if (fallbackResult.error) throw fallbackResult.error;
        items = (fallbackResult.data || []).map(item => ({ ...item, batches: [] }));
        error = null;
      }
    }

    if (error) throw error;

    // Calculate valuation for each item
    const valuedItems = items.map(item => {
      const activeBatches = (item.batches || []).filter(b => b.status === 'active');

      // FIFO value (sum of batch values)
      const fifoValue = activeBatches.reduce(
        (sum, b) => sum + (b.quantity_available * b.unit_cost),
        0
      );

      // Total quantity from batches
      const batchQuantity = activeBatches.reduce(
        (sum, b) => sum + b.quantity_available,
        0
      );

      // Weighted average cost
      const weightedAvgCost = batchQuantity > 0
        ? fifoValue / batchQuantity
        : item.unit_cost;

      // Average value (using weighted average)
      const avgValue = item.current_stock * weightedAvgCost;

      return {
        id: item.id,
        sku: item.sku,
        name: item.name,
        category: item.category,
        unit: item.unit,
        current_stock: item.current_stock,
        batch_count: activeBatches.length,
        fifo_value: Math.round(fifoValue * 100) / 100,
        weighted_avg_cost: Math.round(weightedAvgCost * 100) / 100,
        avg_value: Math.round(avgValue * 100) / 100,
        sale_price: item.sale_price,
        potential_revenue: item.sale_price ? item.current_stock * item.sale_price : null,
      };
    });

    // Calculate totals
    const totals = {
      totalItems: valuedItems.length,
      totalStock: valuedItems.reduce((sum, i) => sum + i.current_stock, 0),
      totalBatches: valuedItems.reduce((sum, i) => sum + i.batch_count, 0),
      totalFifoValue: Math.round(
        valuedItems.reduce((sum, i) => sum + i.fifo_value, 0) * 100
      ) / 100,
      totalAvgValue: Math.round(
        valuedItems.reduce((sum, i) => sum + i.avg_value, 0) * 100
      ) / 100,
      totalPotentialRevenue: Math.round(
        valuedItems.reduce((sum, i) => sum + (i.potential_revenue || 0), 0) * 100
      ) / 100,
    };

    return { data: valuedItems, totals, error: null };
  } catch (error) {
    console.error('Get inventory valuation error:', error);
    return { data: null, totals: null, error };
  }
};

/**
 * Consume inventory from specific manually-selected batches
 * Used when FIFO override is needed (e.g., ISO 13485 traceability)
 * @param {string} inventoryId - Inventory item ID
 * @param {Array} batchSelections - Array of { batchId, quantity }
 * @param {Object} options - Consumption options
 * @param {string} options.reason - Reason for consumption
 * @param {string} options.projectId - Project ID (optional)
 * @param {string} options.taskId - Task ID (optional)
 * @param {string} options.notes - Additional notes
 * @param {string} options.justification - Justification for skipping FIFO
 * @returns {Promise<{data, consumedBatches, totalCost, error}>}
 */
export const consumeFromSpecificBatches = async (inventoryId, batchSelections, options = {}) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    if (!batchSelections || batchSelections.length === 0) {
      throw new Error('Debe seleccionar al menos un lote');
    }

    // Build JSONB array for the DB function
    const selectionsJsonb = batchSelections
      .filter(s => s.quantity > 0)
      .map(s => ({
        batch_id: s.batchId,
        quantity: s.quantity,
      }));

    if (selectionsJsonb.length === 0) {
      throw new Error('Debe seleccionar al menos un lote con cantidad mayor a 0');
    }

    // Build notes with justification
    let fullNotes = options.notes || '';
    if (options.justification) {
      fullNotes = `[SELECCION MANUAL] Justificacion: ${options.justification}. ${fullNotes}`.trim();
    } else {
      fullNotes = `[SELECCION MANUAL] ${fullNotes}`.trim();
    }

    const { data, error } = await supabase.rpc('consume_specific_batches', {
      p_inventory_id: inventoryId,
      p_batch_selections: selectionsJsonb,
      p_project_id: options.projectId || null,
      p_task_id: options.taskId || null,
      p_reason: options.reason || 'consumption',
      p_notes: fullNotes,
      p_created_by: user.id,
    });

    if (error) throw error;

    // The function returns a JSONB object
    if (!data.success) {
      throw new Error(data.message || 'Error al consumir lotes seleccionados');
    }

    return {
      data,
      consumedBatches: data.batches_affected,
      totalCost: Math.round((data.total_cost || 0) * 100) / 100,
      error: null,
    };
  } catch (error) {
    console.error('Consume specific batches error:', error);
    return { data: null, consumedBatches: null, totalCost: 0, error };
  }
};

/**
 * Preview FIFO consumption without executing
 * Shows which batches would be consumed and at what cost
 * @param {string} inventoryId - Inventory item ID
 * @param {number} quantity - Quantity to consume
 * @returns {Promise<{preview, totalCost, error}>}
 */
export const previewFIFOConsumption = async (inventoryId, quantity) => {
  try {
    const { data: batches, error: batchError } = await getInventoryBatches(inventoryId, { activeOnly: true });
    if (batchError) throw batchError;

    if (!batches || batches.length === 0) {
      return { preview: [], totalCost: 0, sufficient: false, error: null };
    }

    const totalAvailable = batches.reduce((sum, b) => sum + b.quantity_available, 0);
    const sufficient = totalAvailable >= quantity;

    let remaining = Math.min(quantity, totalAvailable);
    const preview = [];
    let totalCost = 0;

    for (const batch of batches) {
      if (remaining <= 0) break;

      const consumeFromBatch = Math.min(remaining, batch.quantity_available);
      const batchCost = consumeFromBatch * batch.unit_cost;

      preview.push({
        batch_id: batch.id,
        batch_number: batch.batch_number,
        quantity: consumeFromBatch,
        unit_cost: batch.unit_cost,
        total_cost: Math.round(batchCost * 100) / 100,
        received_at: batch.received_at,
        supplier_name: batch.purchase?.supplier?.name,
      });

      totalCost += batchCost;
      remaining -= consumeFromBatch;
    }

    return {
      preview,
      totalCost: Math.round(totalCost * 100) / 100,
      totalAvailable,
      sufficient,
      error: null,
    };
  } catch (error) {
    console.error('Preview FIFO consumption error:', error);
    return { preview: null, totalCost: 0, sufficient: false, error };
  }
};

const inventoryService = {
  listInventory,
  getInventoryItem,
  createInventoryItem,
  updateInventoryItem,
  deleteInventoryItem,
  recordStockMovement,
  getStockMovements,
  getLowStockItems,
  getInventorySummary,
  bulkImportInventory,
  uploadInventoryImage,
  deleteInventoryImage,
  updateInventoryImage,
  removeInventoryImage,
  // Batch-related functions
  getInventoryBatches,
  consumeInventoryFIFO,
  consumeFromSpecificBatches,
  adjustBatch,
  getInventoryValuation,
  previewFIFOConsumption,
  MOVEMENT_REASONS,
  MOVEMENT_REASON_LABELS,
};

export default inventoryService;
